'use client';

import { useLayoutEffect, useRef, type RefObject } from 'react';
import {
  type SkeletonMorphBlock,
  type SkeletonMorphChannel,
  placeSkeletonGhosts,
  recordSkeletonBlocks,
  settleThenMorph,
  takeFreshSkeletonRecording,
  touchSkeletonRecording,
} from '@/lib/skeleton-morph';

/** On <html> while a transcript skeleton has been on screen. */
const SKELETON_SHOWN_ATTR = 'data-thread-skeleton-shown';

/** Keeps a record of where this skeleton's blocks sit while it is mounted. */
export function useSkeletonMorphSource(
  ref: RefObject<HTMLElement | null>,
  channel: SkeletonMorphChannel = 'transcript'
) {
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    recordSkeletonBlocks(root, channel);
    // A skeleton that replaces this one shows at once (globals.css).
    if (channel === 'transcript') {
      document.documentElement.setAttribute(SKELETON_SHOWN_ATTR, '');
    }
    // Without ResizeObserver (old browsers, jsdom) the mount-time record
    // stands; the morph then lands from the first layout.
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => recordSkeletonBlocks(root, channel));
    observer?.observe(root);
    return () => {
      observer?.disconnect();
      touchSkeletonRecording(channel);
    };
  }, [ref, channel]);
}

/** Where each transcript skeleton block's real counterpart lives. */
const TRANSCRIPT_TARGET_SELECTORS: Record<string, string> = {
  user: '[data-org-user-bubble]',
  work: '[data-assistant-work-summary-header]',
  card: '[data-assistant-work-files-viewer]',
  reply: '[data-assistant-message]',
  actions: '[data-assistant-response-primary-actions]',
};

function isOnScreen(element: Element): boolean {
  const rect = element.getBoundingClientRect();
  return rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
}

/**
 * The real element each block drew, from the latest turn on screen: the last
 * visible user bubble, then the first match after it for every other role.
 */
function getTranscriptTargets(
  container: HTMLElement,
  blocks: SkeletonMorphBlock[]
): Array<HTMLElement | null> {
  const visible = (selector: string) =>
    Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
      isOnScreen
    );
  const anchor = visible(TRANSCRIPT_TARGET_SELECTORS.user ?? '').at(-1);
  const used = new Set<HTMLElement>();
  return blocks.map((block) => {
    const selector = TRANSCRIPT_TARGET_SELECTORS[block.role];
    if (!selector) return null;
    if (block.role === 'user') return anchor ?? null;
    const target =
      visible(selector).find(
        (element) =>
          !used.has(element) &&
          (!anchor ||
            Boolean(
              anchor.compareDocumentPosition(element) &
                Node.DOCUMENT_POSITION_FOLLOWING
            ))
      ) ?? null;
    if (target) used.add(target);
    return target;
  });
}

/**
 * When real rows first replace a skeleton the user was looking at, morph the
 * skeleton's blocks onto them. Content rendered on the server (no skeleton
 * seen) or a later update never morphs.
 */
export function useSkeletonMorphTarget(
  containerRef: RefObject<HTMLElement | null>,
  hasContent: boolean,
  {
    channel = 'transcript',
    getTargets = getTranscriptTargets,
  }: {
    channel?: SkeletonMorphChannel;
    /**
     * The real element each recorded block morphs onto, index for index;
     * a missing one fades in place.
     */
    getTargets?: (
      container: HTMLElement,
      blocks: SkeletonMorphBlock[]
    ) => Array<HTMLElement | null>;
  } = {}
) {
  const settledRef = useRef(false);
  useLayoutEffect(() => {
    if (settledRef.current || !hasContent) return undefined;
    settledRef.current = true;
    // Content took over: the next cold skeleton fades in again.
    if (channel === 'transcript') {
      document.documentElement.removeAttribute(SKELETON_SHOWN_ATTR);
    }
    const container = containerRef.current;
    const blocks = takeFreshSkeletonRecording(channel);
    if (!container || !blocks) return undefined;
    // The content hides before paint while ghosts hold the skeleton's
    // place; they morph once the opening scroll and late layout settle.
    // No cleanup: a StrictMode re-run must not cancel the one morph, and
    // the morph always restores the content's opacity.
    container.style.opacity = '0';
    const ghosts = placeSkeletonGhosts(blocks);
    settleThenMorph(
      blocks,
      ghosts,
      () => getTargets(container, blocks),
      container
    );
    return undefined;
  }, [containerRef, hasContent, channel, getTargets]);
}
